import { InventoryTab, ORIGIN_TAB_MAP } from "@/DataPages/components/Inventory/constants";
import isEqual from "lodash-es/isEqual";
import { createContext, Dispatch, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import { SelectionItem, useSelectionContext } from "../../../../Common/context/selection/SelectionContext";
import { getLogger } from "@/Common/helpers/getLogger";
import { objectsMatchProperties } from "@/Common/helpers/objectsTs";
import uniqBy from "lodash-es/uniqBy";

const log = getLogger("InventoryFilterContext");

export interface InventoryFilterItemBase {
	_id: string;
	origin: InventoryTab;
	table: string;
	vinNumber: string;
}

export interface InventoryFilterItem extends InventoryFilterItemBase {
	commissionCycleId: string;
	payPeriodId: string;
}

export interface InventoryFilterItemInput extends InventoryFilterItemBase {
	commissionCycleId?: string;
	payPeriodId?: string;
}

export interface InventoryFilterContextValue {
	selectedItems: SelectionItem[];
	filterItems: InventoryFilterItem[];
	addToFilter: (item: InventoryFilterItemInput, isSingleSelect: boolean) => void;
	removeFromFilter: (item: InventoryFilterItemInput) => void;
	clearFilterSelection: (filterObjects?: Partial<Omit<InventoryFilterItemInput, "_id" | "vinNumber">>[]) => void;
	addManyToFilter: (items: InventoryFilterItemInput[]) => void;
	removeManyFromFilter: (items: InventoryFilterItemInput[]) => void;
	setOnClearCallback: (callback: () => void) => void;
	payPeriodId: string;
	setPayPeriodId: Dispatch<React.SetStateAction<string>>;
	commissionCycleId: string;
	setCommissionCycleId: Dispatch<React.SetStateAction<string>>;
}

const InventoryFilterContext = createContext<InventoryFilterContextValue | null>(null);

type InventoryFilterProps = {
	children: ReactNode;
};

const canAddItemFromOrigin = (newItem: InventoryFilterItem, items: InventoryFilterItem[]) => {
	if (!items.length) {
		return true;
	}

	const currentOrigins = items.map((i) => i.origin);

	const allowedOrigins = ORIGIN_TAB_MAP[newItem.origin];
	if (!allowedOrigins) {
		return false;
	}

	return (
		allowedOrigins.some((o) => currentOrigins.includes(o)) &&
		items.some((element) => element.commissionCycleId === newItem.commissionCycleId && element.payPeriodId === newItem.payPeriodId)
	);
};

const uniqByIteratee = ({ table, _id, commissionCycleId, payPeriodId, origin }: InventoryFilterItem) =>
	[table, _id, commissionCycleId, payPeriodId, origin].join("|");

const generateNewFilterList = (prev: InventoryFilterItem[], items: InventoryFilterItem[], isSingleSelect = false) => {
	if (isSingleSelect) {
		return uniqBy(
			[...prev.filter((element) => element.table !== items[0].table && element.origin !== items[0].origin), items[0]],
			uniqByIteratee
		);
	}
	return uniqBy([...prev, ...items], uniqByIteratee);
};

const buildItem = (item: InventoryFilterItemInput, commissionCycleId: string, payPeriodId: string): InventoryFilterItem => ({
	commissionCycleId,
	payPeriodId,
	...item,
});

const buildItems = (items: InventoryFilterItemInput[], commissionCycleId: string, payPeriodId: string): InventoryFilterItem[] => {
	return items.map((item) => buildItem(item, commissionCycleId, payPeriodId));
};

const extractActiveFilterKeys = (obj: Partial<Omit<InventoryFilterItem, "_id" | "vinNumber">>) => {
	return [
		...(obj.table ? (["table"] as const) : []),
		...(obj.origin ? (["origin"] as const) : []),
		...(obj.commissionCycleId ? (["commissionCycleId"] as const) : []),
		...(obj.payPeriodId ? (["payPeriodId"] as const) : []),
	];
};

function InventoryFilterProvider({ children }: Readonly<InventoryFilterProps>) {
	const [filterItems, setFilterItems] = useState<InventoryFilterItem[]>([]);

	const listenersRef = useRef<(() => void)[]>([]);

	const {
		selectedItems,
		clearSelection,
		addToSelection,
		addManyToSelection,
		removeFromSelection,
		removeManyFromSelection,
		commissionCycleId,
		payPeriodId,
		setCommissionCycleId,
		setPayPeriodId,
	} = useSelectionContext();

	const clearFilterSelection = useCallback(
		(filterObjects?: Partial<Omit<InventoryFilterItem, "_id" | "vinNumber">>[]) => {
			log("Clearing filter selection %O", filterObjects);
			if (!filterObjects) {
				setFilterItems([]);
				clearSelection();
				listenersRef.current.forEach((listener) => listener());
				return;
			}
			setFilterItems((prev) =>
				prev.filter((item) => !filterObjects.some((obj) => objectsMatchProperties(obj, item, extractActiveFilterKeys(obj))))
			);
			clearSelection(filterObjects);
		},
		[setFilterItems, clearSelection]
	);

	const setOnClearCallback = useCallback(
		(callback: () => void) => {
			log("Setting on clear callback");
			listenersRef.current.push(callback);
		},
		[listenersRef]
	);

	const addToFilter = useCallback(
		(item: InventoryFilterItemInput, isSingleSelect: boolean) => {
			log("Adding item to filter %O", item);
			const completeItem = buildItem(item, commissionCycleId, payPeriodId);
			setFilterItems((prev) => {
				if (canAddItemFromOrigin(completeItem, prev)) {
					return generateNewFilterList(prev, [completeItem], isSingleSelect);
				}
				return prev;
			});
			addToSelection(completeItem, isSingleSelect);
		},
		[setFilterItems, commissionCycleId, payPeriodId, addToSelection]
	);

	const addManyToFilter = useCallback(
		(items: InventoryFilterItemInput[]) => {
			log("Adding items to filter %O", items);
			const completeItems = buildItems(items, commissionCycleId, payPeriodId);
			setFilterItems((prev) => {
				const ableToAdd = completeItems.filter((item) => canAddItemFromOrigin(item, prev));
				if (ableToAdd.length) {
					return generateNewFilterList(prev, completeItems);
				}
				return prev;
			});
			addManyToSelection(completeItems);
		},
		[setFilterItems, commissionCycleId, payPeriodId, addToSelection]
	);

	const removeFromFilter = useCallback(
		(item: InventoryFilterItemInput) => {
			log("Removing item from filter %O", item);
			const completeItem = buildItem(item, commissionCycleId, payPeriodId);
			setFilterItems((prev) => {
				const newList = prev.filter((filteredItem) => !isEqual(filteredItem, completeItem));
				if (!newList.length) {
					clearSelection();
				}
				return newList;
			});
			removeFromSelection(completeItem);
		},
		[setFilterItems, commissionCycleId, payPeriodId, removeFromSelection, clearSelection]
	);

	const removeManyFromFilter = useCallback(
		(items: InventoryFilterItemInput[]) => {
			log("Removing item from filter %O", items);

			const completeItems = buildItems(items, commissionCycleId, payPeriodId);

			setFilterItems((prev) => {
				const newList = prev.filter((filteredItem) => !completeItems.some((item) => isEqual(filteredItem, item)));
				if (!newList.length) {
					clearSelection();
				}
				return newList;
			});
			removeManyFromSelection(completeItems);
		},
		[setFilterItems, commissionCycleId, payPeriodId, removeFromSelection, clearSelection]
	);

	const value = useMemo(
		() => ({
			selectedItems,
			filterItems,
			addToFilter,
			addManyToFilter,
			removeFromFilter,
			removeManyFromFilter,
			clearFilterSelection,
			setOnClearCallback,
			payPeriodId,
			setPayPeriodId,
			commissionCycleId,
			setCommissionCycleId,
		}),
		[filterItems, addToFilter, removeFromFilter, clearFilterSelection, setOnClearCallback]
	);
	return <InventoryFilterContext.Provider value={value}>{children}</InventoryFilterContext.Provider>;
}

function useInventoryContext() {
	const context = useContext(InventoryFilterContext);
	if (!context) {
		throw new Error("useInventoryContext must be used within a InventoryProvider");
	}
	return context;
}

export { InventoryFilterProvider, useInventoryContext };
